/**
 * INTERNAL-ONLY price-quote math for the admin Calculator tab. Pure functions,
 * no I/O — fully unit-testable. NEVER surface this (numbers, feature add-on
 * rates, or the tool itself) in apps/docs or apps/marketing.
 *
 * Pricing is metered per unit per month (PMPM members, or PEPM employees — the
 * unit only changes labels, never the math). There are no volume tiers: the base
 * rate is seeded from the default floor (`DEFAULT_RATE_CENTS`, $0.25) and set per
 * quote by the feature plan. Each enabled feature adds an incremental per-unit
 * rate (NOT a flat fee). Upcharges (e.g. dedicated on-prem hosting) are flat $/mo
 * line items added after discount.
 */
import {
  DEFAULT_RATE_CENTS,
  FEATURES,
  FEATURE_KEYS,
  type FeatureDef,
  type FeatureKey,
} from '@commissionsight/shared';

/** Billing unit — per member per month, or per employee per month. */
export type QuoteUnit = 'pmpm' | 'pepm';

/** A feature row: when enabled, `addOnRate` ($/unit/month) stacks onto the base rate. */
export interface FeatureLine {
  key: FeatureKey;
  label: string;
  /** Not built yet — flagged so the operator doesn't quote it by accident. */
  roadmap: boolean;
  enabled: boolean;
  /** Incremental per-unit-per-month dollars added when enabled. */
  addOnRate: number;
}

/** A flat monthly upcharge (dollars), e.g. dedicated on-prem hosting. */
export interface Upcharge {
  label: string;
  amount: number;
}

/** Who the quote is for — presentation metadata, not part of the price math. */
export interface QuoteContact {
  company: string;
  contact: string;
  email: string;
}

export interface QuoteInput {
  unit: QuoteUnit;
  /** Book size — distinct members (PMPM) or employees (PEPM). */
  count: number;
  /** Base per-unit rate in dollars (seeded from the tier engine, overridable). */
  baseRate: number;
  features: FeatureLine[];
  /** Percentage discount (0–100) applied to the metered software subtotal only. */
  discountPct: number;
  upcharges: Upcharge[];
}

export interface QuoteResult {
  /** baseRate + Σ enabled feature add-ons, per unit / month. */
  effectiveRate: number;
  /** count × effectiveRate. */
  meteredMonthly: number;
  /** meteredMonthly × discountPct/100. */
  discountAmount: number;
  /** meteredMonthly − discountAmount. */
  meteredNet: number;
  /** Σ upcharge amounts (flat $/mo). */
  upchargesTotal: number;
  /** meteredNet + upchargesTotal. */
  monthlyTotal: number;
  /** monthlyTotal × 12 — run-rate projection only (the book changes monthly). */
  annualTotal: number;
  /** monthlyTotal ÷ count — effective blended per-unit rate. */
  blendedRate: number;
  /** The enabled feature rows, for itemized display/summary. */
  enabledFeatures: FeatureLine[];
}

/** Feature rows derived from the canonical entitlements registry, all off, $0 add-on. */
export function defaultFeatureLines(): FeatureLine[] {
  return FEATURE_KEYS.map((key) => {
    const def: FeatureDef = FEATURES[key];
    return { key, label: def.label, roadmap: def.roadmap === true, enabled: false, addOnRate: 0 };
  });
}

/** The default base per-unit rate in dollars ($0.25) — the floor a quote starts
 *  from before feature add-ons; the operator overrides it per plan. */
export function seedBaseRate(): number {
  return DEFAULT_RATE_CENTS / 100;
}

/** Unit label helpers so the UI/summary stay consistent. */
export function unitNoun(unit: QuoteUnit): string {
  return unit === 'pepm' ? 'employee' : 'member';
}
export function unitAbbr(unit: QuoteUnit): string {
  return unit === 'pepm' ? 'PEPM' : 'PMPM';
}

const safeNum = (n: number): number => (Number.isFinite(n) ? n : 0);

/** Compute a full quote from operator inputs. Pure — no rounding except for safety. */
export function computeQuote(input: QuoteInput): QuoteResult {
  const count = Math.max(0, Math.floor(safeNum(input.count)));
  const baseRate = Math.max(0, safeNum(input.baseRate));
  const discountPct = Math.min(100, Math.max(0, safeNum(input.discountPct)));

  const enabledFeatures = input.features.filter((f) => f.enabled);
  const featureRate = enabledFeatures.reduce(
    (sum, f) => sum + Math.max(0, safeNum(f.addOnRate)),
    0,
  );
  const effectiveRate = baseRate + featureRate;

  const meteredMonthly = count * effectiveRate;
  const discountAmount = meteredMonthly * (discountPct / 100);
  const meteredNet = meteredMonthly - discountAmount;

  const upchargesTotal = input.upcharges.reduce(
    (sum, u) => sum + Math.max(0, safeNum(u.amount)),
    0,
  );
  const monthlyTotal = meteredNet + upchargesTotal;

  return {
    effectiveRate,
    meteredMonthly,
    discountAmount,
    meteredNet,
    upchargesTotal,
    monthlyTotal,
    annualTotal: monthlyTotal * 12,
    blendedRate: count > 0 ? monthlyTotal / count : 0,
    enabledFeatures,
  };
}

const usd = (n: number): string =>
  n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
/** Per-unit rates are sub-dollar — show 4 decimals so $0.10 add-ons are legible. */
const rate = (n: number): string =>
  n.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 4 });

/** Plain-text quote summary for the Copy button — pasteable into email/proposal. */
export function quoteSummaryText(
  input: QuoteInput,
  result: QuoteResult,
  contact?: QuoteContact,
): string {
  const noun = unitNoun(input.unit);
  const abbr = unitAbbr(input.unit);
  const lines: string[] = [];
  lines.push('CommissionSight — Price Quote');
  if (contact && (contact.company || contact.contact || contact.email)) {
    lines.push('');
    if (contact.company) lines.push(`Company: ${contact.company}`);
    if (contact.contact) lines.push(`Contact: ${contact.contact}`);
    if (contact.email) lines.push(`Email: ${contact.email}`);
  }
  lines.push('');
  lines.push(`Book size: ${input.count.toLocaleString('en-US')} ${noun}s`);
  lines.push(`Base rate: ${rate(input.baseRate)} ${abbr}`);
  if (result.enabledFeatures.length) {
    lines.push('Feature add-ons:');
    for (const f of result.enabledFeatures) {
      lines.push(`  + ${f.label}: ${rate(f.addOnRate)} ${abbr}${f.roadmap ? ' (roadmap)' : ''}`);
    }
  }
  lines.push(`Effective rate: ${rate(result.effectiveRate)} ${abbr}`);
  lines.push(`Metered subtotal: ${usd(result.meteredMonthly)}/mo`);
  if (input.discountPct > 0) {
    lines.push(`Discount (${input.discountPct}%): -${usd(result.discountAmount)}/mo`);
  }
  if (input.upcharges.length) {
    lines.push('Upcharges:');
    for (const u of input.upcharges) {
      lines.push(`  + ${u.label || 'Upcharge'}: ${usd(u.amount)}/mo`);
    }
  }
  lines.push('');
  lines.push(`Monthly total: ${usd(result.monthlyTotal)}`);
  lines.push(`Annual total (run-rate est.): ${usd(result.annualTotal)}`);
  lines.push(`Effective blended rate: ${rate(result.blendedRate)} ${abbr}`);
  return lines.join('\n');
}

/** Escape user-entered text so it can't break (or inject into) the print document. */
function esc(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Build a standalone, print-ready HTML document for a quote — contact block,
 * itemized line items, discount, upcharges, and totals. Opened in its own
 * window so Print captures only the quote, not the admin console. `dateStr` is
 * passed in (keeps this pure/testable; the caller formats "today").
 */
export function buildQuoteHtml(
  input: QuoteInput,
  result: QuoteResult,
  contact: QuoteContact,
  dateStr: string,
): string {
  const noun = unitNoun(input.unit);
  const abbr = unitAbbr(input.unit);
  const row = (label: string, value: string, opts?: { strong?: boolean; muted?: boolean }) =>
    `<tr class="${opts?.strong ? 'strong' : ''}${opts?.muted ? ' muted' : ''}">
      <td class="label">${label}</td><td class="value">${value}</td></tr>`;

  const featureRows = result.enabledFeatures
    .map((f) =>
      row(
        `&nbsp;&nbsp;+ ${esc(f.label)}${f.roadmap ? ' <span class="tag">roadmap</span>' : ''}`,
        `+${rate(f.addOnRate)} ${abbr}`,
        { muted: true },
      ),
    )
    .join('');
  const upchargeRows = input.upcharges
    .map((u) => row(`&nbsp;&nbsp;+ ${esc(u.label || 'Upcharge')}`, `+${usd(u.amount)}/mo`))
    .join('');

  const contactLines = [
    contact.company && `<div class="big">${esc(contact.company)}</div>`,
    contact.contact && `<div>${esc(contact.contact)}</div>`,
    contact.email && `<div><a href="mailto:${esc(contact.email)}">${esc(contact.email)}</a></div>`,
  ]
    .filter(Boolean)
    .join('');

  return `<!doctype html>
<html lang="en"><head><meta charset="utf-8" />
<title>CommissionSight quote${contact.company ? ` — ${esc(contact.company)}` : ''}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
    color: #171717; margin: 0; padding: 40px; max-width: 720px; }
  .brand { display:flex; align-items:center; gap:10px; font-size:20px; font-weight:700; letter-spacing:-.01em; }
  .brand .sight { color:#059669; }
  .logo { width:26px; height:26px; border-radius:7px; background:#0B0F0E; position:relative; flex:0 0 auto; }
  .logo i { position:absolute; left:6px; height:3.5px; border-radius:2px; }
  .logo .g { top:7px; width:14px; background:#34D399; }
  .logo .y { top:12px; width:10px; background:#FBBF24; }
  .logo .r { top:17px; width:6px;  background:#FB7185; }
  h1 { font-size:13px; text-transform:uppercase; letter-spacing:.08em; color:#737373; margin:28px 0 4px; }
  .meta { color:#737373; font-size:13px; }
  .for { margin:18px 0 26px; padding:16px 18px; background:#f5f5f5; border-radius:12px; }
  .for .big { font-size:16px; font-weight:600; }
  .for div { font-size:14px; line-height:1.5; }
  .for a { color:#059669; text-decoration:none; }
  table { width:100%; border-collapse:collapse; font-size:14px; }
  td { padding:7px 0; border-bottom:1px solid #f0f0f0; vertical-align:top; }
  td.value { text-align:right; font-variant-numeric:tabular-nums; white-space:nowrap; padding-left:20px; }
  tr.muted td { color:#737373; border-bottom:none; padding:3px 0; }
  tr.strong td { font-weight:700; font-size:16px; border-bottom:none; border-top:2px solid #171717; padding-top:12px; }
  .total { margin-top:22px; padding:18px 20px; background:#ecfdf5; border:1px solid #d1fae5; border-radius:12px;
    display:flex; align-items:baseline; justify-content:space-between; }
  .total .k { font-size:12px; text-transform:uppercase; letter-spacing:.06em; color:#047857; font-weight:600; }
  .total .v { font-size:30px; font-weight:700; color:#047857; font-variant-numeric:tabular-nums; }
  .total .sub { font-size:12px; color:#059669; }
  .tag { font-size:10px; background:#fef3c7; color:#92400e; padding:1px 6px; border-radius:99px; vertical-align:middle; }
  footer { margin-top:34px; padding-top:16px; border-top:1px solid #eee; font-size:11px; color:#a3a3a3; }
  @page { margin: 16mm; }
  @media print {
    body { padding:0; max-width:none; }
    .total { -webkit-print-color-adjust:exact; print-color-adjust:exact; }
  }
</style></head>
<body>
  <div class="brand">
    <span class="logo"><i class="g"></i><i class="y"></i><i class="r"></i></span>
    Commission<span class="sight">Sight</span>
  </div>
  <h1>Price Quote</h1>
  <div class="meta">${esc(dateStr)}</div>
  ${contactLines ? `<div class="for">${contactLines}</div>` : '<div style="height:18px"></div>'}

  <div class="total">
    <div><div class="k">Monthly total</div>
      <div class="sub">${rate(result.blendedRate)} ${abbr} blended · ${input.count.toLocaleString('en-US')} ${noun}s</div></div>
    <div class="v">${usd(result.monthlyTotal)}</div>
  </div>

  <h1 style="margin-top:28px">Breakdown</h1>
  <table>
    ${row('Book size', `${input.count.toLocaleString('en-US')} ${noun}s`)}
    ${row(`Base rate (${abbr})`, rate(input.baseRate))}
    ${featureRows}
    ${row(`Effective rate (${abbr})`, rate(result.effectiveRate), { strong: result.enabledFeatures.length === 0 })}
    ${row('Metered subtotal', `${usd(result.meteredMonthly)}/mo`)}
    ${input.discountPct > 0 ? row(`Discount (${input.discountPct}%)`, `-${usd(result.discountAmount)}/mo`) : ''}
    ${upchargeRows}
    ${row('Monthly total', usd(result.monthlyTotal), { strong: true })}
    ${row('Annual total (run-rate est.)', usd(result.annualTotal), { muted: true })}
  </table>

  <footer>
    Internal estimate prepared by CommissionSight. Pricing is metered ${abbr} on the account's
    distinct ${noun} count, which changes month over month — figures are an estimate, not a binding
    offer, and annual totals are a run-rate projection.
  </footer>
</body></html>`;
}
